// One row model for the Game Planner's "Select Starting Pitcher" step.
//
// This composes engines that already exist rather than adding new maths:
//   getPitcherEligibility  rest, eligible date, pitches remaining
//   forecastInnings        how deep an arm can go under the league's cap
//   playerSeasonStats      S%, FPS%, WHIP, ERA, K, BB, R
//
// The only genuinely new thing here is the RECOMMENDATION, and it is deliberately
// explainable: a coach should be able to read the reason and agree or disagree.
// Nothing here decides anything. The coach picks; this only sorts and explains.

import { getPitcherEligibility, PitchCountRuleset, PitchingLogEntry } from './pitch-count-rules';
import { forecastInnings } from './pitching-plan';

export interface PitcherSeasonLine {
  sPct: number | null;
  fpsPct: number | null;
  whip: number | null;
  era: number | null;
  k: number;
  bb: number;
  r: number;
  ip: number;
}

export interface PitcherLastOuting {
  date: string;
  pitches: number | null;
  ip: number | null;
  k: number | null;
  bb: number | null;
  r: number | null;
  opponent: string | null;
}

export interface PitcherReadiness {
  player: any;
  eligible: boolean;
  /** 'ready' full go, 'limited' eligible but short on pitches, 'rest' unavailable. */
  status: 'ready' | 'limited' | 'rest';
  /** Short right-hand label: "Ready", "35p left", "Rest to Sep 8". */
  statusLabel: string;
  daysRest: number | null;
  /** Forecast innings under the cap, from his own pitches per inning. */
  forecastIP: number | null;
  pitchesRemaining: number | null;
  season: PitcherSeasonLine | null;
  lastOuting: PitcherLastOuting | null;
  /** True for the single suggested arm. */
  recommended: boolean;
  /** Bench-coach sentence for the recommended arm only. */
  recommendationReason: string | null;
}

const dayMs = 86_400_000;

/** True innings from a log entry, tolerating both the innings and outs shapes. */
function entryInnings(e: PitchingLogEntry): number | null {
  if (e.outsPitched != null) return e.outsPitched / 3;
  if (e.inningsPitched != null) return e.inningsPitched;
  return null;
}

/**
 * Pitches per inning from the log. Only a fallback: the season rollup's own
 * `pIp` is preferred because it comes from GameChanger's per-game figures, while
 * the log only records what Dugout itself counted during a live game.
 */
function pitchesPerInning(log: PitchingLogEntry[]): number | null {
  let pitches = 0, innings = 0;
  log.forEach(e => {
    const ip = entryInnings(e);
    if (ip != null && ip > 0 && e.pitchCount > 0) { pitches += e.pitchCount; innings += ip; }
  });
  return innings > 0 ? pitches / innings : null;
}

/**
 * Build one readiness row per pitcher on the roster.
 *
 * @param players       roster, already filtered to those available today
 * @param seasonStats   the `stats` map from buildSeasonStats (NESTED shape: each
 *                      entry carries a full `.pitching` bucket). This is the same
 *                      source PitchingCommandCenter reads, so the Game Planner and
 *                      the Stats tab can never disagree about a pitcher.
 * @param gamesById     game id to { date, opponent, stats } for last-outing detail
 */
export function buildPitcherReadiness(
  players: any[],
  ruleset: PitchCountRuleset | undefined,
  ageGroup: string,
  gameDate: Date,
  tournamentId: string | undefined,
  seasonStats: Map<string, any> | undefined,
  gamesById: Map<string, any>,
  hasPitchCounts: boolean,
): PitcherReadiness[] {
  const ageRule: any = ruleset?.ageGroups?.[ageGroup] || ruleset?.ageGroups?.['all'];

  const rows: PitcherReadiness[] = players
    .filter((p: any) => (p.positions?.P || p.ratings?.P || 0) > 0)
    .map((p: any) => {
      const log: PitchingLogEntry[] = p.pitchingLog || [];
      const sorted = [...log].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const last = sorted[0];

      let eligible = true;
      let status: PitcherReadiness['status'] = 'ready';
      let statusLabel = 'Ready';
      let pitchesRemaining: number | null = null;

      if (hasPitchCounts && ruleset) {
        const el = getPitcherEligibility(log, ruleset, ageGroup, gameDate, 0, tournamentId, true);
        eligible = el.eligible;

        if (!eligible) {
          status = 'rest';
          const d = el.restStatus?.eligibleDate ? new Date(el.restStatus.eligibleDate) : null;
          statusLabel = d
            ? `Rest to ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : 'On rest';
        } else {
          if (el.pitchesRemaining < 999) pitchesRemaining = el.pitchesRemaining;
          // "Limited" means eligible but carrying less than a full day's allowance,
          // which is what a coach actually needs to see before naming a starter.
          const full = ageRule?.dailyMax ?? null;
          if (pitchesRemaining != null && full && pitchesRemaining < full) {
            status = 'limited';
            statusLabel = `${pitchesRemaining}p left`;
          } else if (el.warningReason) {
            status = 'limited';
            statusLabel = 'Limited';
          }
        }
      }

      const daysRest = last
        ? Math.floor((gameDate.getTime() - new Date(last.date).setHours(0, 0, 0, 0)) / dayMs)
        : null;

      // Prefer the season rollup's pitches-per-inning (GameChanger's own figure);
      // fall back to the log only when the rollup has no sample yet.
      const pit = seasonStats?.get(p.id)?.pitching;
      const pIp = pit?.pIp ?? pitchesPerInning(log);
      const capForForecast = pitchesRemaining ?? ageRule?.dailyMax ?? null;
      const fc = pIp != null && capForForecast != null
        ? forecastInnings(pIp, { dailyMax: capForForecast, dailyMaxInnings: ageRule?.dailyMaxInnings })
        : null;

      // `so` is strikeouts THROWN and `bb` walks ALLOWED in this bucket, which is
      // why the batting-side fields of the same name must not be used here.
      const hasSeason = !!pit && ((pit.ip || 0) > 0 || (pit.pitches || 0) > 0);
      const season: PitcherSeasonLine | null = hasSeason
        ? {
            sPct: pit.sPct != null && pit.sPct > 0 ? pit.sPct : null,
            fpsPct: pit.fpsPct != null && pit.fpsPct > 0 ? pit.fpsPct : null,
            whip: pit.whip != null && pit.whip > 0 ? pit.whip : null,
            era: pit.era != null && pit.era > 0 ? pit.era : null,
            k: pit.so || 0,
            bb: pit.bb || 0,
            r: pit.rAllowed || 0,
            ip: pit.ipTrue ?? pit.ip ?? 0,
          }
        : null;

      let lastOuting: PitcherLastOuting | null = null;
      if (last) {
        const g = gamesById.get(last.gameId);
        const gs = g?.stats?.[p.id];
        lastOuting = {
          date: last.date,
          pitches: last.pitchCount ?? null,
          ip: entryInnings(last),
          k: gs?.soAllowed ?? null,
          bb: gs?.bbAllowed ?? null,
          r: gs?.rAllowed ?? null,
          opponent: g?.opponent ?? null,
        };
      }

      return {
        player: p, eligible, status, statusLabel, daysRest,
        forecastIP: fc?.forecastIP ?? null,
        pitchesRemaining, season, lastOuting,
        recommended: false, recommendationReason: null,
      };
    });

  // ── Recommendation ────────────────────────────────────────────────────────
  // Only among arms that can actually pitch today.
  //
  // Rosters routinely carry more players with a P rating than have ever actually
  // pitched, so "do we know anything about this arm" is a real factor, not a
  // tiebreaker. A kid with a track record should be suggested ahead of one the
  // app has never seen throw, even if their scouting grades match.
  const hasRecord = (r: PitcherReadiness) => r.season != null || r.daysRest != null;

  const candidates = rows.filter(r => r.status !== 'rest');
  if (candidates.length > 0) {
    const score = (r: PitcherReadiness) => {
      const grade = (r.player.positions?.P || r.player.ratings?.P || 0) / 5;   // 0-1
      // Never thrown reads NEUTRAL, not maximally fresh. Treating an unknown arm
      // as fully rested would float it above a proven one on nothing but silence.
      const rest = r.daysRest == null ? 0.5 : Math.min(r.daysRest, 5) / 5;
      const command = r.season?.sPct != null ? Math.min(r.season.sPct / 0.7, 1) : 0.5;
      const capacity = r.status === 'limited' ? 0.5 : 1;
      const known = hasRecord(r) ? 1 : 0;
      return grade * 0.35 + rest * 0.15 + command * 0.15 + capacity * 0.1 + known * 0.25;
    };
    const best = [...candidates].sort((a, b) => score(b) - score(a))[0];
    best.recommended = true;

    const bits: string[] = [];
    if (best.daysRest != null && best.daysRest >= 4) bits.push(`${best.daysRest} days rest`);
    if (best.season?.sPct != null) bits.push(`${Math.round(best.season.sPct * 100)}% strikes`);
    if (best.forecastIP != null) bits.push(`good for about ${best.forecastIP.toFixed(1)} innings`);

    const first = best.player.name?.split(' ')[0] || best.player.name;
    if (bits.length) {
      best.recommendationReason = `${first} looks like your arm today: ${bits.join(', ')}.`;
    } else if (!hasRecord(best)) {
      // Say plainly that this is a scouting call, so a coach knows the app is
      // not claiming evidence it does not have.
      best.recommendationReason = `${first} is your highest-rated arm. No pitching data logged yet, so this is off your scouting grades.`;
    } else {
      best.recommendationReason = `${first} is available and rated highest on the mound.`;
    }
  }

  // Available first, then the recommendation, then arms we actually know
  // something about, then the coach's scouting grade.
  const rank = (r: PitcherReadiness) => (r.status === 'rest' ? 2 : 0) - (r.recommended ? 1 : 0);
  return rows.sort((a, b) =>
    rank(a) - rank(b) ||
    Number(hasRecord(b)) - Number(hasRecord(a)) ||
    (b.player.positions?.P || 0) - (a.player.positions?.P || 0),
  );
}
